/**
 * Which reading of the dice a roll gets.
 *
 * `coreResolution` is the default and each entry in `coreResolution.variants`
 * overrides it, field by field, for the roll types it names (see
 * `ResolutionVariantSchema` for why it is shaped that way). This file is the one
 * place that turns "a Contest roll in Trophy Dark" into the single flat
 * resolution the runtime reads, so the adapter and the animated-dice path in
 * `resolution.ts` cannot come to different answers about the same roll.
 *
 * Kept apart from `adapter-from-rules.ts` because `resolution.ts` needs it too,
 * and that file should not have to load the whole runtime to learn which dice a
 * system throws.
 */
/**
 * The reading a roll of this type gets.
 *
 * The first variant naming the type wins. `adapterFromRules` refuses a type
 * named by two variants, so for any document that builds there is only ever
 * one candidate, and "first" never has to break a tie.
 */
export function readingForRoll(resolution, rollType) {
    const { variants, ...core } = resolution;
    const variant = rollType === undefined ? undefined : variants?.find((v) => v.appliesTo.includes(rollType));
    if (!variant)
        return { resolution: core };
    return { resolution: overlayVariant(core, variant), variant };
}
/**
 * The core block with a variant's reading fields laid over it.
 *
 * A field the variant leaves out is inherited. A field set to `undefined` —
 * which a hand-built TypeScript literal can do and a parsed document cannot — is
 * treated as left out, so it does not erase what the core block said.
 */
export function overlayVariant(core, variant) {
    const { id: _id, name: _name, description: _description, appliesTo: _appliesTo, ...reading } = variant;
    const merged = { ...core };
    for (const [key, value] of Object.entries(reading)) {
        if (value !== undefined)
            merged[key] = value;
    }
    delete merged.variants;
    return merged;
}
//# sourceMappingURL=resolution-variants.js.map